import { Seletor } from '../Filtros';
import {
  BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao, type FiltroAtivo,
} from '../BarraDeLista';
import { Periodo, type ValorDoPeriodo } from '../Periodo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { useBusca, type Cliente } from '../cadastros';

/**
 * O portal, visto de dentro.
 *
 * ── A pergunta com que esta tela abre ────────────────────────────────
 *
 * «O que já está pronto e o cliente ainda não recebeu?»
 *
 * É a única pergunta do sistema que nenhuma outra tela responde. A fila
 * mostra o que falta ensaiar; a assinatura, o que falta aprovar; a
 * medição, o que falta medir. Um laudo emitido, assinado e parado é
 * trabalho terminado que ninguém entregou — e ele não aparecia em lugar
 * nenhum até esta tela existir.
 *
 * Por isso a aba «A entregar» é a primeira, e vem com a lista carregada.
 * A aba de cadastro é a segunda: mexe-se nela uma vez por cliente.
 */

interface Documento {
  tipo: 'relatorio' | 'recebimento';
  documento_id: string;
  os_id: string;
  os_numero: string;
  numero: string;
  revisao: number;
  titulo: string;
  situacao: 'pronto' | 'entregue' | 'em_producao';
  emitido_em: string | null;
  disponibilizado_em: string | null;
  disponibilizado_por_nome: string | null;
  recolhido_em: string | null;
  motivo_recolhimento: string | null;
  acessos: string;
}

interface Acesso {
  cliente_id: string; cliente: string; concedido_em: string;
}

interface Pessoa {
  id: string;
  email: string;
  nome: string;
  cargo: string | null;
  telefone: string | null;
  situacao: 'ativo' | 'inativo' | 'bloqueado';
  ultimo_acesso: string | null;
  ultima_abertura: string | null;
  clientes: string;
  acessos: Acesso[];
  /** 0240 (RN-230): o último pedido de código desse e-mail (30 dias). */
  ultimo_pedido_em: string | null;
  /** Se aquele pedido gerou código — `false` é pedir e não receber. */
  ultimo_pedido_atendido: boolean | null;
}

/**
 * Acesso que pede código e não recebe — onda 285, `RN-230`.
 *
 * O portal só emite código para quem está ativo E tem cliente vinculado.
 * Para todos os outros a tela de entrada mostra a mesma frase («Se este
 * e-mail estiver cadastrado… o código chegou»), de propósito: diferenciar
 * entregaria a lista de clientes da Testesolo. O lado de FORA tem de ficar
 * calado — o de DENTRO, não. Em 06/10 os dois acessos do Humberto estavam
 * «ativo» e sem cliente (o vínculo saiu com a exclusão de um cliente de
 * teste), e esta tela dizia só «nenhum» numa coluna e «ativo» na outra.
 */
const semCliente = (p: Pessoa) => p.situacao === 'ativo' && Number(p.clientes) === 0;

const TOM_SITUACAO: Record<string, Tom> = {
  pronto: 'atencao', entregue: 'ok', em_producao: 'neutro',
};
const NOME_SITUACAO: Record<string, string> = {
  pronto: 'A entregar', entregue: 'No portal', em_producao: 'Em produção',
};
const TOM_PESSOA: Record<string, Tom> = {
  ativo: 'ok', inativo: 'neutro', bloqueado: 'critico',
};

const dataHora = (v: string | null) => (v
  ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  : '—');

export function Portal() {
  const [aba, setAba] = useState<'entregar' | 'pessoas'>('entregar');

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Portal do cliente</h1>
          <p className="sub">
            O que já foi entregue, o que falta entregar, e quem tem acesso —
            o emitido mais recente primeiro
          </p>
        </div>
        <a
          className="botao secundario"
          href="https://portal.testesolo.com.br"
          target="_blank"
          rel="noreferrer"
        >
          Abrir o portal
        </a>
      </div>

      <div className="abas-lista" role="tablist" aria-label="Portal do cliente">
        {([
          ['entregar', 'A entregar'],
          ['pessoas', 'Quem tem acesso'],
        ] as const).map(([chave, texto]) => (
          <button
            key={chave}
            type="button"
            role="tab"
            className={aba === chave ? 'aqui' : ''}
            aria-selected={aba === chave}
            onClick={() => setAba(chave)}
          >
            {texto}
          </button>
        ))}
      </div>

      {aba === 'entregar' ? <AEntregar /> : <Pessoas />}
    </div>
  );
}

// ── A entregar ──────────────────────────────────────────────────────

function AEntregar() {
  const [linhas, setLinhas] = useState<Documento[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [recolhendo, setRecolhendo] = useState<Documento | null>(null);
  const [situacao, setSituacao] = useState<string | null>(null);
  /* A barra do padrão (onda 272): busca e período no que já chegou. */
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState<ValorDoPeriodo>({ de: '', ate: '' });
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (situacao) q.set('situacao', situacao);
      setLinhas(await api<Documento[]>(`/portal/publicacao?${q}`));
    } catch (e) { setErro(e); }
  }, [situacao]);

  useEffect(() => { void carregar(); }, [carregar]);

  const disponibilizar = async (d: Documento) => {
    try {
      await api(`/portal/${d.tipo}/${d.documento_id}/disponibilizar`,
        { metodo: 'POST' });
      await carregar();
    } catch (e) { setErro(e); }
  };

  const b = busca.trim().toLowerCase();
  const filtrados = (linhas ?? []).filter((d) => {
    const dia = d.emitido_em ? d.emitido_em.slice(0, 10) : null;
    if (periodo.de && (!dia || dia < periodo.de)) return false;
    if (periodo.ate && (!dia || dia > periodo.ate)) return false;
    if (b && ![d.titulo, d.numero, d.os_numero].join(' ').toLowerCase().includes(b)) return false;
    return true;
  });
  const pag = usePaginacao(filtrados);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!linhas) return <Carregando />;

  const ativos: FiltroAtivo[] = [];
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') });
  if (periodo.de || periodo.ate) {
    ativos.push({ chave: 'per', texto: `Emitidos: ${periodo.de || '…'} – ${periodo.ate || '…'}`.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3/$2/$1'), tirar: () => setPeriodo({ de: '', ate: '' }) });
  }

  return (
    <>
      <BarraDeLista rotulo="Filtros dos documentos do portal">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Documento, número ou OS" rotulo="Buscar documento do portal" />
        <Seletor naBarra rotulo="Situação" valor={situacao}
          opcoes={[{ valor: 'entregue', texto: 'Já no portal' }]}
          aoMudar={setSituacao} todos="A entregar" />
        <Periodo valor={periodo} aoMudar={setPeriodo} rotulo="Emitidos"
          atalhos={['semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']} />
      </BarraDeLista>
      <FiltrosAtivos itens={ativos}
        aoLimpar={() => { setBusca(''); setPeriodo({ de: '', ate: '' }); }}
        contagem={`${filtrados.length} ${filtrados.length === 1 ? 'documento' : 'documentos'} · ${situacao === 'entregue' ? 'no portal' : 'a entregar'}`} />

      {recolhendo && (
        <DialogoRecolher
          documento={recolhendo}
          aoFechar={() => setRecolhendo(null)}
          aoPronto={() => { setRecolhendo(null); void carregar(); }}
        />
      )}

      <div className="cartao">
        {filtrados.length === 0 ? (
          <Vazio>
            {linhas.length > 0
              ? 'Nenhum documento com estes filtros. Use «Limpar filtros».'
              : situacao === 'entregue'
                ? 'Nenhum documento disponibilizado ainda.'
                : 'Nada parado. Todo documento emitido já foi ao cliente.'}
          </Vazio>
        ) : (
          <div className="rola">
            <table className="dados pilha">
              <thead>
                <tr>
                  <th scope="col">Documento</th>
                  <th scope="col" className="col-opcional">Emitido</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Ação</th>
                </tr>
              </thead>
              <tbody>
                {pag.visiveis.map((d) => (
                  <tr key={`${d.tipo}:${d.documento_id}`}>
                    <th scope="row">
                      <strong>{d.titulo}</strong>
                      <span className="sub">
                        {d.numero} · R{String(d.revisao).padStart(2, '0')} ·{' '}
                        <Link to={`/os/${d.os_id}`}>OS {d.os_numero}</Link>
                      </span>
                      {d.disponibilizado_em && (
                        <span className="sub">
                          entregue em {dataHora(d.disponibilizado_em)}
                          {d.disponibilizado_por_nome
                            ? ` por ${d.disponibilizado_por_nome}` : ''}
                          {' · '}
                          {Number(d.acessos) > 0
                            ? `${d.acessos} abertura(s) pelo cliente`
                            : 'ainda não aberto'}
                        </span>
                      )}
                    </th>
                    <td className="col-opcional">{dataHora(d.emitido_em)}</td>
                    <td>
                      <Selo tom={TOM_SITUACAO[d.situacao] ?? 'neutro'}>
                        {NOME_SITUACAO[d.situacao] ?? d.situacao}
                      </Selo>
                    </td>
                    <td>
                      {!pode('relatorio.disponibilizar') ? (
                        <span className="nada">sem permissão</span>
                      ) : d.situacao === 'entregue' ? (
                        <button
                          type="button"
                          className="botao secundario"
                          onClick={() => setRecolhendo(d)}
                        >
                          Recolher
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="botao"
                          onClick={() => void disponibilizar(d)}
                        >
                          Disponibilizar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <RodapeDaLista {...pag} teto={500} ordem="emitido em ↓" />

        <p className="rodape-regra">
          Disponibilizar é o ato de ENTREGAR: a partir dele o cliente vê o
          documento no portal, e é dessa data que o prazo de guarda da
          amostra passa a contar. Recolher tira do portal, exige motivo
          escrito e não apaga o registro de quem já abriu.
        </p>
      </div>
    </>
  );
}

function DialogoRecolher({ documento, aoFechar, aoPronto }: {
  documento: Documento; aoFechar(): void; aoPronto(): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setEnviando(true); setErro(null);
    try {
      await api(`/portal/${documento.tipo}/${documento.documento_id}/recolher`,
        { metodo: 'POST', corpo: { motivo: motivo.trim() } });
      aoPronto();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo="Recolher do portal" aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <ErroDaApi erro={erro} />
        <Aviso tipo="atencao" titulo="O cliente pode já ter baixado.">
          Recolher tira o documento da tela dele, mas não desfaz o download.
          Se o documento estava errado, o caminho é emitir uma revisão.
        </Aviso>
        <Campo
          rotulo="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          dica="Fica na trilha e é o que alguém vai ler daqui a um ano."
          autoFocus
          required
        />
        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="botao"
                  disabled={enviando || motivo.trim().length < 5}>
            {enviando ? 'Recolhendo…' : 'Recolher'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

// ── Quem tem acesso ─────────────────────────────────────────────────

function Pessoas() {
  const [linhas, setLinhas] = useState<Pessoa[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [nova, setNova] = useState(false);
  const [vincular, setVincular] = useState<Pessoa | null>(null);
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      setLinhas(await api<Pessoa[]>(`/portal/pessoas?${q}`));
    } catch (e) { setErro(e); }
  }, [busca]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  const mudarSituacao = async (p: Pessoa, situacao: Pessoa['situacao']) => {
    try {
      await api(`/portal/pessoas/${p.id}`, {
        metodo: 'PATCH',
        corpo: { situacao, motivo: `situação alterada para ${situacao}` },
      });
      await carregar();
    } catch (e) { setErro(e); }
  };

  if (erro) return <ErroDaApi erro={erro} />;

  return (
    <>
      {nova && (
        <FormPessoa
          aoFechar={() => setNova(false)}
          aoPronto={() => { setNova(false); void carregar(); }}
        />
      )}
      {vincular && (
        <FormPessoa
          pessoa={vincular}
          aoFechar={() => setVincular(null)}
          aoPronto={() => { setVincular(null); void carregar(); }}
        />
      )}

      <BarraDeLista rotulo="Filtros dos acessos">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Nome ou e-mail" rotulo="Buscar acesso" />
        {pode('portal.gerir') && (
          <button type="button" className="botao barra-acao" onClick={() => setNova(true)}>
            Novo acesso
          </button>
        )}
      </BarraDeLista>

      {linhas && linhas.some(semCliente) && (
        <Aviso tipo="atencao"
          titulo={(() => {
            const n = linhas.filter(semCliente).length;
            return n === 1
              ? '1 acesso ativo sem cliente — não recebe o código'
              : `${n} acessos ativos sem cliente — não recebem o código`;
          })()}>
          <p>
            O portal só manda código para quem tem pelo menos um cliente
            vinculado. Para os outros, a tela de entrada mostra a mesma frase
            de sempre — de propósito, para não revelar quem é cliente da
            Testesolo —, e a pessoa fica esperando um e-mail que não sai. Use
            «Vincular cliente» na linha marcada.
          </p>
        </Aviso>
      )}

      {!linhas ? <Carregando /> : (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Acessos ao portal</h2>
            <span className="sub">{linhas.length} {linhas.length === 1 ? 'pessoa' : 'pessoas'} · ordem: nome</span>
          </div>

          {linhas.length === 0 ? (
            <Vazio>
              Ninguém cadastrado ainda. O acesso é pelo e-mail que o cliente
              já usa para receber os relatórios — nenhuma senha é criada.
            </Vazio>
          ) : (
            <div className="rola">
              <table className="dados pilha">
                <thead>
                  <tr>
                    <th scope="col">Pessoa</th>
                    <th scope="col">Clientes</th>
                    <th scope="col" className="col-opcional">Último acesso</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((p) => (
                    <tr key={p.id} data-pessoa={p.email}
                      data-sem-cliente={semCliente(p) ? 'sim' : undefined}>
                      <th scope="row">
                        <strong>{p.nome}</strong>
                        <span className="sub">{p.email}</span>
                        {p.cargo && <span className="sub">{p.cargo}</span>}
                      </th>
                      <td>
                        {p.acessos.length === 0
                          ? <span className="nada">nenhum</span>
                          : p.acessos.map((a) => (
                            <span key={a.cliente_id} className="sub">
                              {a.cliente}
                            </span>
                          ))}
                        {p.acessos.length === 0 && pode('portal.gerir') && (
                          <button type="button" className="botao secundario estreito"
                            style={{ marginTop: '.4rem' }}
                            onClick={() => setVincular(p)}>
                            Vincular cliente
                          </button>
                        )}
                      </td>
                      <td className="col-opcional">
                        {dataHora(p.ultimo_acesso)}
                        {p.ultima_abertura && (
                          <span className="sub">
                            abriu documento em {dataHora(p.ultima_abertura)}
                          </span>
                        )}
                        {p.ultimo_pedido_em && (
                          <span className={p.ultimo_pedido_atendido ? 'sub' : 'sub aviso-linha'}
                            data-ultimo-pedido={p.ultimo_pedido_atendido ? 'atendido' : 'sem-codigo'}>
                            pediu código em {dataHora(p.ultimo_pedido_em)} ·{' '}
                            {p.ultimo_pedido_atendido ? 'código emitido' : 'nenhum código emitido'}
                          </span>
                        )}
                      </td>
                      <td>
                        {semCliente(p) ? (
                          <>
                            <Selo tom="atencao">Sem cliente</Selo>
                            <span className="sub">não recebe código</span>
                          </>
                        ) : (
                          <Selo tom={TOM_PESSOA[p.situacao] ?? 'neutro'}>
                            {p.situacao}
                          </Selo>
                        )}
                        {pode('portal.gerir') && (
                          <button
                            type="button"
                            className="botao secundario"
                            style={{ marginTop: '.4rem' }}
                            onClick={() => void mudarSituacao(
                              p, p.situacao === 'ativo' ? 'bloqueado' : 'ativo')}
                          >
                            {p.situacao === 'ativo' ? 'Bloquear' : 'Reativar'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="rodape-regra">
            O código só sai para quem está ativo e tem cliente vinculado; a
            tela de entrada responde igual para todos, então é aqui que se
            vê quem pediu e não recebeu. Bloquear corta as sessões abertas
            na hora — não só o próximo login. É por isso que a sessão do portal é um registro no banco
            e não um crachá assinado que vale até vencer.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * O acesso novo — e, com `pessoa`, o cliente a mais para um acesso que já
 * existe (285, `RN-230`).
 *
 * A rota é a mesma (`POST /portal/pessoas`): o e-mail é único, e para quem
 * já existe ela só acrescenta (ou ressuscita) o vínculo. Por isso «Vincular
 * cliente» abre esta janela com o e-mail travado e o nome preenchido — o
 * único campo a decidir é o cliente.
 */
function FormPessoa({ aoFechar, aoPronto, pessoa }: {
  aoFechar(): void; aoPronto(): void; pessoa?: Pessoa;
}) {
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [nome, setNome] = useState(pessoa?.nome ?? '');
  const [email, setEmail] = useState(pessoa?.email ?? '');
  const [cargo, setCargo] = useState(pessoa?.cargo ?? '');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setEnviando(true); setErro(null);
    try {
      await api('/portal/pessoas', {
        metodo: 'POST',
        corpo: {
          email: email.trim().toLowerCase(),
          nome: nome.trim(),
          cargo: cargo.trim() || undefined,
          clienteId: cliente?.id,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  return (
    <Dialogo aberto aoFechar={aoFechar}
      titulo={pessoa ? 'Vincular cliente ao acesso' : 'Novo acesso ao portal'}
      rotulo={pessoa ? pessoa.email : undefined}>
      <form onSubmit={enviar}>
        <ErroDaApi erro={erro} />

        <BuscaDoCliente cliente={cliente} aoEscolher={setCliente} />

        <Campo
          rotulo="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          dica={pessoa
            ? 'O acesso já existe: escolha o cliente que esta pessoa vai ver.'
            : 'O mesmo que já recebe os relatórios. É por ele que o código chega.'}
          readOnly={!!pessoa}
          required
        />

        <Campo
          rotulo="Nome"
          value={nome}
          onChange={(e) => setNome(e.currentTarget.value)}
          dica="Como a pessoa assina. Aparece no registro de quem abriu cada documento."
          required
        />

        <Campo
          rotulo="Cargo"
          value={cargo}
          onChange={(e) => setCargo(e.currentTarget.value)}
          dica="Opcional."
        />

        <Aviso tipo="info" titulo="Não existe senha.">
          A pessoa digita o e-mail no portal e recebe um código de seis
          dígitos, válido por dez minutos e por um uso. Nada para ela
          esquecer, nada para vazar.
        </Aviso>

        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="botao"
                  disabled={enviando || !cliente || !email.trim() || !nome.trim()}>
            {enviando ? 'Gravando…' : pessoa ? 'Vincular cliente' : 'Conceder acesso'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * O cliente do acesso, escolhido pela BUSCA — onda 218.
 *
 * «Quando clicamos em novo acesso, aparece a listagem de todos os
 *  clientes em bloco. Isso fica inviável, pois a quantidade de clientes
 *  não para de crescer» — Humberto, 28/09/2026.
 *
 * Aqui havia um `Escolha`: um botão por cliente, TODOS, carregados de uma
 * vez. Com quinze já ocupava metade do diálogo; com duzentos (o teto da
 * rota) empurraria e-mail e nome para fora da tela — e o cliente certo
 * teria de ser achado com os olhos numa parede de botões.
 *
 * O gesto passa a ser o mesmo da nova OS e do recebimento
 * (`EscolhaDeCliente`): digita-se parte do nome, o servidor devolve até
 * oito, escolhe-se um, e o campo mostra o escolhido com «Trocar». Quem
 * já usa o sistema não aprende nada novo.
 *
 * **Sem «cadastrar cliente» no fim da busca**, ao contrário da OS: acesso
 * ao portal é para quem já é cliente — a OS que ele vai ver tem de
 * existir antes. Cliente que não aparece é sinal de erro de digitação ou
 * de cadastro que falta, e o lugar do cadastro é Cadastros.
 *
 * A dica diz «razão social ou nome fantasia», e não CNPJ, porque é o que
 * a rota `GET /clientes?busca=` casa — prometer o CNPJ seria mandar a
 * pessoa digitar o que não acha nada.
 */
function BuscaDoCliente({ cliente, aoEscolher }: {
  cliente: Cliente | null; aoEscolher(c: Cliente | null): void;
}) {
  const rota = useCallback(
    (alvo: string) => `/clientes?busca=${encodeURIComponent(alvo)}`, []);
  const { texto, setTexto, achados, semPermissao } = useBusca<Cliente>(rota);
  const campo = useRef<HTMLInputElement>(null);

  /*
   * O cursor nasce na busca. `autoFocus` sozinho não basta: o
   * `showModal()` do diálogo roda DEPOIS e leva o foco ao primeiro
   * focável, que é o ✕. Um quadro depois, a busca o retoma — e o mesmo
   * vale ao voltar de «Trocar».
   */
  useEffect(() => {
    if (cliente) return undefined;
    const q = requestAnimationFrame(() => campo.current?.focus());
    return () => cancelAnimationFrame(q);
  }, [cliente]);

  if (cliente) {
    return (
      <div className="campo" data-cliente-escolhido={cliente.id}>
        <span className="rotulo">Cliente</span>
        <div className="linha">
          <div className="item" style={{ cursor: 'default' }}>
            <span className="cod">{cliente.razao_social}</span>
            {cliente.cnpj_cpf && <span className="meta">{cliente.cnpj_cpf}</span>}
          </div>
          <button type="button" className="botao secundario estreito"
            style={{ flex: '0 0 auto' }}
            onClick={() => { aoEscolher(null); setTexto(''); }}>
            Trocar
          </button>
        </div>
        <span className="dica">A pessoa vai ver as OS deste cliente, e só as dele.</span>
      </div>
    );
  }

  const vazio = !!achados && achados.length === 0 && texto.trim().length >= 2;

  return (
    <div className="campo">
      <label className="rotulo" htmlFor="portal-busca-cliente">Cliente</label>
      <input
        id="portal-busca-cliente"
        ref={campo}
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Razão social ou nome fantasia"
        autoComplete="off"
      />
      {semPermissao && <span className="dica">Seu perfil não consulta clientes.</span>}
      {achados && achados.length > 0 && (
        <div className="lista" style={{ marginTop: '.5rem' }} data-busca-cliente>
          {achados.map((c) => (
            <button key={c.id} type="button" className="item"
              onClick={() => aoEscolher(c)}>
              <span className="cod">{c.razao_social}</span>
              {c.cnpj_cpf && <span className="meta">{c.cnpj_cpf}</span>}
            </button>
          ))}
        </div>
      )}
      {vazio && !semPermissao && (
        <span className="dica">
          Nenhum cliente com «{texto.trim()}». Confira a grafia — ou o
          cadastro, em Cadastros → Clientes.
        </span>
      )}
      {!achados && !semPermissao && (
        <span className="dica">
          Digite ao menos duas letras. A pessoa vai ver as OS deste
          cliente, e só as dele.
        </span>
      )}
    </div>
  );
}
